import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { topics } from "@/mocks/topics";

const hotKeywords = ["巡演", "悬疑剧", "时装周", "Y2K", "Citywalk"];

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [topicOpen, setTopicOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const isHome = location.pathname === "/";
  const transparent = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
    setTopicOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (searchOpen) {
      const timer = window.setTimeout(() => inputRef.current?.focus(), 60);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [searchOpen]);

  const submitSearch = () => {
    const q = keyword.trim();
    if (!q) return;
    setSearchOpen(false);
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  const today = new Date().toLocaleDateString("zh-CN", {
    year: "numeric",
    month: "long",
    day: "numeric",
    weekday: "long",
  });

  const navItems = [
    { label: "首页", to: "/" },
    ...categories.map((c) => ({ label: c.name, to: `/category/${c.slug}` })),
    { label: "关于我们", to: "/about" },
  ];

  const linkBase =
    "relative whitespace-nowrap px-1 py-2 text-sm font-medium transition-colors duration-200";

  return (
    <header className="sticky top-0 z-50">
      {/* 顶部信息条 */}
      <div className="hidden bg-secondary-900 md:block">
        <div className="flex h-9 w-full items-center justify-between px-4 text-[12px] text-secondary-200 md:px-6 lg:px-8">
          <span className="tracking-wide">{today} · 噜噜社，每天为你精选娱乐与潮流</span>
          <div className="flex items-center gap-4">
            <span className="text-secondary-300">关注我们</span>
            <div className="flex items-center gap-3 text-base">
              <a href="https://weibo.com" target="_blank" rel="noreferrer" aria-label="微博" className="cursor-pointer transition-colors hover:text-primary-400">
                <i className="ri-weibo-fill" />
              </a>
              <a href="https://www.douyin.com" target="_blank" rel="noreferrer" aria-label="抖音" className="cursor-pointer transition-colors hover:text-primary-400">
                <i className="ri-tiktok-fill" />
              </a>
              <a href="https://www.xiaohongshu.com" target="_blank" rel="noreferrer" aria-label="小红书" className="cursor-pointer transition-colors hover:text-primary-400">
                <i className="ri-book-marked-fill" />
              </a>
              <a href="https://www.bilibili.com" target="_blank" rel="noreferrer" aria-label="哔哩哔哩" className="cursor-pointer transition-colors hover:text-primary-400">
                <i className="ri-play-circle-fill" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* 主导航 */}
      <div
        className={`w-full transition-all duration-300 ${
          transparent
            ? "bg-transparent"
            : "border-b border-background-200 bg-background-50/95 backdrop-blur"
        }`}
      >
        <div className="flex w-full items-center gap-4 px-4 md:gap-8 md:px-6 lg:px-8">
          <Link to="/" className="flex shrink-0 cursor-pointer items-center gap-2 py-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-lg font-bold text-background-50">
              噜
            </span>
            <span className="flex flex-col leading-none">
              <span
                className={`text-lg font-extrabold tracking-tight ${
                  transparent ? "text-background-50" : "text-foreground-950"
                }`}
              >
                噜噜社
              </span>
              <span
                className={`text-[10px] font-medium uppercase tracking-[0.2em] ${
                  transparent ? "text-background-200" : "text-foreground-400"
                }`}
              >
                LULU CLUB
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 md:flex">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `${linkBase} ${isActive ? "text-primary-500" : transparent ? "text-background-100 hover:text-primary-400" : "text-foreground-700 hover:text-primary-500"}`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <div
              className="relative"
              onMouseEnter={() => setTopicOpen(true)}
              onMouseLeave={() => setTopicOpen(false)}
            >
              <button
                type="button"
                onClick={() => setTopicOpen((v) => !v)}
                className={`${linkBase} flex cursor-pointer items-center gap-1 ${
                  transparent ? "text-background-100 hover:text-primary-400" : "text-foreground-700 hover:text-primary-500"
                }`}
              >
                专题
                <i className={`ri-arrow-down-s-line text-base transition-transform ${topicOpen ? "rotate-180" : ""}`} />
              </button>
              {topicOpen && (
                <div className="absolute left-1/2 top-full z-50 w-64 -translate-x-1/2 pt-2">
                  <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50 p-2 shadow-sm">
                    {topics.map((t) => (
                      <Link
                        key={t.id}
                        to={`/topic/${t.id}`}
                        className="flex cursor-pointer items-center justify-between rounded-md px-3 py-2 transition-colors hover:bg-primary-50"
                      >
                        <span className="text-sm font-medium text-foreground-900">{t.title}</span>
                        <i className="ri-arrow-right-s-line text-foreground-400" />
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSearchOpen((v) => !v)}
              aria-label="搜索"
              className={`flex h-9 w-9 cursor-pointer items-center justify-center rounded-full transition-colors ${
                transparent
                  ? "text-background-50 hover:bg-background-50/20"
                  : "text-foreground-700 hover:bg-background-200"
              }`}
            >
              <i className="ri-search-line text-lg" />
            </button>
            <Link
              to="/about"
              className="hidden whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 md:inline-flex"
            >
              联系我们
            </Link>
            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="菜单"
              className={`flex h-9 w-9 cursor-pointer items-center justify-center rounded-full transition-colors md:hidden ${
                transparent ? "text-background-50 hover:bg-background-50/20" : "text-foreground-800 hover:bg-background-200"
              }`}
            >
              <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl`} />
            </button>
          </div>
        </div>
      </div>

      {/* 搜索面板 */}
      {searchOpen && (
        <div className="w-full border-b border-background-200 bg-background-50">
          <div className="w-full px-4 py-4 md:px-6 lg:px-8">
            <div className="flex items-center gap-3 rounded-lg border border-background-300 bg-background-100 px-3 py-2 focus-within:border-primary-400">
              <i className="ri-search-line text-lg text-foreground-400" />
              <input
                ref={inputRef}
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submitSearch()}
                placeholder="搜索音乐、影视、时尚、潮流文化…"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              <button
                type="button"
                onClick={submitSearch}
                className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-1.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                搜索
              </button>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-xs text-foreground-500">热门搜索</span>
              {hotKeywords.map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setKeyword(k)}
                  className="cursor-pointer rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900 transition-colors hover:bg-primary-100 hover:text-primary-800"
                >
                  {k}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 移动端菜单 */}
      {mobileOpen && (
        <div className="border-b border-background-200 bg-background-50 md:hidden">
          <nav className="flex flex-col px-4 py-2">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `border-b border-background-100 py-3 text-sm font-medium ${isActive ? "text-primary-500" : "text-foreground-800"}`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <div className="py-3">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-foreground-400">专题</p>
              <div className="flex flex-col gap-1">
                {topics.map((t) => (
                  <Link key={t.id} to={`/topic/${t.id}`} className="flex items-center gap-2 py-1.5 text-sm text-foreground-800">
                    <i className="ri-bookmark-line text-primary-500" />
                    {t.title}
                  </Link>
                ))}
              </div>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}